import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Link, useNavigate } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import { jwtDecode } from "jwt-decode";
import { loginUser } from "../../redux/slices/authSlice";
import api from "../../api/axios";
import { fetchUser } from "../../redux/slices/profileSlice";
import { announceUserChange } from "../../utilities/currentUser";
import { LuEye, LuEyeOff, LuLock, LuMail } from "react-icons/lu";
import Navbar from "../../components/companyLandingComponents/Navbar/Navbar";
import "./auth.css";
import BottomFooter from "../../components/Footer/BottomFooter/BottomFooter";

const LoginPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading } = useSelector((state) => state.auth);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const handleSSOLogin = () => {
    const state = Math.random().toString(36).substring(2, 15);
    localStorage.setItem("sso_state", state);

    const ssoUrl = process.env.REACT_APP_ICONIC_UNITY_SSO_URL || "http://localhost:3000/sso/authorize";
    const clientId = process.env.REACT_APP_ICONIC_GREENLAND_COMPANY_ID || "2";
    const redirectUri = window.location.origin + "/sso-callback";
    
    window.location.href = `${ssoUrl}?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&state=${state}`;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const result = await dispatch(loginUser({ email, password }));

    if (loginUser.fulfilled.match(result)) {
  const data = result.payload;

  if (data.message === "Your account is not approved yet!") {
    toast.warning("Your account is not approved yet!");
    return;
  }

  const user = data.user;
  const token = data.token;
  if (!user || !token) {
    toast.error("Invalid credentials..!");
    return;
  }

  localStorage.setItem("token", token);
  localStorage.setItem("user", JSON.stringify(user));
  announceUserChange(); // the sidebar reads companies from the stored user
  dispatch(fetchUser());
  toast.success(`Welcome back, ${user.name}!`);

  // ✅ canApprove check করে navigate
  try {
    const res = await api.get("/api/my-approver-status");
    if (res.data.can_approve) {
      navigate("/pending-users");
    } else {
      navigate("/profile");
    }
  } catch {
    navigate("/profile");
  }
} else {
        toast.error(result.payload || "Login failed!");
      }
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong!");
    }
  };

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#f8f9fa" }}>
      <Navbar />

      {/* Dynamic Background Overlay */}
      <div
        className="position-fixed start-0 end-0 top-0 bottom-0"
        style={{
          background:
            'url("/assets/images/iconicback.jpg") center/cover no-repeat',
          zIndex: 0,
        }}
      ></div>

      <div
        className="container position-relative d-flex align-items-center justify-content-center"
        style={{
          zIndex: 1,
          minHeight: "calc(100vh - 80px)",
        }}
      >
        <div className="row justify-content-center w-100">
          <div className="col-xxl-8 col-lg-10">
            <div
              className="card border-0 shadow-lg overflow-hidden"
              style={{
                borderRadius: "15px",
                backdropFilter: "blur(10px)",
                backgroundColor: "rgba(255, 255, 255, 0.98)",
              }}
            >
              <div className="row g-0">
                {/* Brand Sidebar (Visible on Desktop) */}
                <div
                  className="col-lg-5 d-none d-lg-flex flex-column align-items-center justify-content-center text-white p-5"
                  style={{ backgroundColor: "#2e86de" }}
                >
                  <img
                    src="/assets/images/greenland.png"
                    alt="logo"
                    className="img-fluid mb-4"
                    style={{
                      maxWidth: "180px",
                    }}
                  />
                  <h3 className="fw-bold text-center welcome-text">
                    Welcome Back
                  </h3>
                  <p className="text-center opacity-75 small">
                    Log in to manage your account and stay updated with your
                    activities.
                  </p>
                  <div className="mt-4 border-top border-light pt-4 w-100 text-center">
                    <small className="d-block mb-3 opacity-75">New here?</small>
                    <Link
                      to="/register"
                      className="btn btn-outline-light rounded-pill px-4"
                    >
                      Create Account
                    </Link>
                  </div>
                </div>

                {/* Login Form Section */}
                <div className="col-lg-7 p-4 p-md-5">
                  <div className="d-lg-none text-center mb-4">
                    <img
                      src="/assets/images/greenland.png"
                      alt="logo"
                      style={{ width: "120px" }}
                    />
                  </div>

                  <h2 className="fw-bold text-dark mb-1">Sign In</h2>
                  <p className="text-muted mb-4">
                    Access your dashboard using email & password
                  </p>

                  <form onSubmit={handleLogin}>
                    <div className="mb-3">
                      <label className="form-label small fw-semibold">
                        Email Address
                      </label>
                      <div className="input-group custom-input-group">
                        <span className="input-group-text bg-light text-muted">
                          <LuMail size={18} />
                        </span>

                        <input
                          className="form-control bg-light"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="name@example.com"
                          required
                        />
                      </div>
                    </div>

                    <div className="mb-3">
                      <div className="d-flex justify-content-between">
                        <label className="form-label small fw-semibold">
                          Password
                        </label>
                      </div>
                      <div className="input-group custom-input-group">
                        <span className="input-group-text bg-light text-muted">
                          <LuLock size={18} />
                        </span>
                        <input
                          type={showPassword ? "text" : "password"}
                          className="form-control bg-light"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Enter Password"
                          required
                        />
                        <span
                          className="input-group-text bg-light border-start-0 text-muted"
                          onClick={() => setShowPassword(!showPassword)}
                          style={{ cursor: "pointer" }}
                        >
                          {showPassword ? (
                            <LuEyeOff size={18} />
                          ) : (
                            <LuEye size={18} />
                          )}
                        </span>
                      </div>
                    </div>
                    <Link
                      to="/forgot-password"
                      size="sm"
                      className="text-primary text-decoration-none small"
                    >
                      Forgot Password?
                    </Link>
                    <div className="mt-4">
                      <button
                        className="btn-style w-100 py-2 fw-bold shadow-sm text-center d-flex align-items-center justify-content-center"
                        type="submit"
                        disabled={loading}
                        style={{ borderRadius: "8px" }}
                      >
                        {loading ? (
                          <span className="spinner-border spinner-border-sm me-2"></span>
                        ) : (
                          <span> Log In</span>
                        )}
                      </button>
                    </div>

                    <div className="mt-3">
                      <button
                        className="btn btn-outline-primary w-100 py-2 fw-bold shadow-sm text-center d-flex align-items-center justify-content-center gap-2 sso-btn"
                        type="button"
                        onClick={handleSSOLogin}
                        style={{
                          borderRadius: "8px",
                          borderColor: "#2e86de",
                          color: "#2e86de",
                          transition: "all 0.2s ease-in-out",
                        }}
                      >
                        <img 
                          src="/assets/images/logo.png" 
                          alt="Iconic Unity" 
                          style={{ height: "20px", width: "auto" }} 
                        />
                        <span>Login with Iconic Unity</span>
                      </button>
                    </div>

                    <div className="position-relative text-center my-4">
                      <hr className="text-muted" />
                      <span className="position-absolute top-50 start-50 translate-middle bg-white px-3 text-muted small">
                        Or continue with
                      </span>
                    </div>

                    <div className="d-flex justify-content-center">
                      <GoogleLogin
                        onSuccess={async (credentialResponse) => {
                          // ... (Your existing logic for Google Login)
                          const res = await api.post("api/auth/google-token", {
                            token: credentialResponse.credential,
                          });
                          if (
                            res.data.message ===
                            "Your account is not approved yet!"
                          ) {
                            toast.warning("Your account is not approved yet!");
                            return;
                          }
                          localStorage.setItem("token", res.data.token);
                          localStorage.setItem(
                            "user",
                            JSON.stringify(res.data.user),
                          );
                          announceUserChange();
                          toast.success(`Welcome ${res.data.user.name}!`);
                          // Same approver check as the email login (works with any/no roles).
                          try {
                            const approver = await api.get(
                              "/api/my-approver-status",
                            );
                            navigate(
                              approver.data.can_approve
                                ? "/pending-users"
                                : "/profile",
                            );
                          } catch {
                            navigate("/profile");
                          }
                        }}
                        onError={() => toast.error("Google Login Failed")}
                      />
                    </div>
                  </form>

                  <div className="text-center mt-5 d-lg-none">
                    <p className="text-muted small">
                      Don't have an account?{" "}
                      <Link to="/register" className="text-primary fw-bold">
                        Sign Up
                      </Link>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .form-control:focus {
          box-shadow: none;
          border-color: #2e86de;
        }
        .input-group-text {
          transition: all 0.2s ease;
        }
        .form-control:focus + .input-group-text {
          border-color: #2e86de;
        }
        .sso-btn:hover {
          background-color: #2e86de !important;
          color: #ffffff !important;
          transform: translateY(-1px);
          box-shadow: 0 4px 6px rgba(46, 134, 222, 0.15);
        }
        .sso-btn:active {
          transform: translateY(0);
        }
      `}</style>

      <BottomFooter></BottomFooter>
    </div>
  );
};

export default LoginPage;
